import cx from "classnames";
import camelCase from "lodash-es/camelCase";
import { ReactNode, useCallback, useId, useRef, useState } from "react";
import { useOverlay, usePreventScroll } from "react-aria";

import useAnalytics from "hooks/useAnalytics";
import { useCssTransition } from "hooks/useCssTransition";
import { TransitionState } from "hooks/useCssTransition/types";
import { withTestId } from "utils/withTestId";

import Box from "../Box";
import { DRAWER_INITIAL_WIDTHS, MAX_DRAWER_WIDTH } from "./constants";
import { DrawerProvider } from "./DrawerContext";
import styles from "./styles.module.css";

const isTestEnvironment = process.env.NODE_ENV === "test";

export type DrawerProps = {
  visible: boolean;
  children: ReactNode;
  onOutsideClick?: () => void;
  className?: string;
  visibleClassName?: string;
  position?: "absoluteRight" | "fixedRight";
  dataTestId?: string;
  variant?: "normal" | "wide";
  isResizable?: boolean;
  onEnteredCallback?: () => void;
  disablePreventScroll?: boolean;
  mountOnEnter?: boolean;
};

const Drawer = ({
  dataTestId,
  visible,
  children,
  onOutsideClick,
  className,
  visibleClassName,
  position = "fixedRight",
  variant = "normal",
  isResizable = false,
  disablePreventScroll = false,
  mountOnEnter = false,
  onEnteredCallback,
}: DrawerProps) => {
  const drawerRef = useRef<HTMLDivElement>(null);
  const defaultDrawerWidth = DRAWER_INITIAL_WIDTHS[variant];
  const [drawerWidth, setDrawerWidth] = useState(defaultDrawerWidth);
  const trackSegmentAnalyticsEvent = useAnalytics();
  const drawerId = useId();

  const handleMouseDown = () => {
    document.addEventListener("mouseup", handleMouseUp, true);
    document.addEventListener("mousemove", handleMouseMove, true);
  };

  const handleMouseUp = () => {
    document.removeEventListener("mouseup", handleMouseUp, true);
    document.removeEventListener("mousemove", handleMouseMove, true);
    trackSegmentAnalyticsEvent("Drawer resized", {
      width: drawerWidth,
    });
  };

  const handleMouseMove = useCallback(
    (e: MouseEvent) => {
      e.preventDefault();

      const offsetRight = document.body.offsetWidth - (e.clientX - document.body.offsetLeft);
      const newWidth = offsetRight;
      if (newWidth > defaultDrawerWidth && newWidth < MAX_DRAWER_WIDTH) {
        setDrawerWidth(newWidth);
      }
    },
    [defaultDrawerWidth]
  );

  usePreventScroll({ isDisabled: !visible || disablePreventScroll });

  useOverlay(
    {
      isOpen: visible,
      onClose: onOutsideClick,
      shouldCloseOnInteractOutside: (element: Element) => {
        return !drawerRef.current?.contains(element);
      },
      isDismissable: visible && !!onOutsideClick,
    },
    drawerRef
  );

  const { state, shouldRender } = useCssTransition({
    visible,
    timeout: 200,
    unmountOnExit: mountOnEnter,
    onEntered: onEnteredCallback,
  });

  if (isTestEnvironment && !shouldRender) {
    return null;
  }

  return (
    <DrawerProvider value={drawerId}>
      <Box
        {...withTestId(dataTestId)}
        direction="column"
        fullWidth
        ref={drawerRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={drawerId}
        className={cx(
          styles.drawer,
          styles[camelCase(`variant-${variant}`)],
          {
            [styles.visible]:
              state === TransitionState.ENTERED || state === TransitionState.ENTERING,
            ...(visibleClassName && {
              [visibleClassName]:
                state === TransitionState.ENTERED || state === TransitionState.ENTERING,
            }),
          },
          state === TransitionState.EXITED && styles.visibilityHidden,
          position && styles[position],
          isResizable && styles.draggable,
          className
        )}
        style={isResizable ? { width: drawerWidth } : undefined}
      >
        {isResizable && (
          <div
            role="button"
            tabIndex={0}
            onMouseDown={() => handleMouseDown()}
            className={styles.dragger}
          />
        )}

        {visible && children}
      </Box>
    </DrawerProvider>
  );
};

Drawer.displayName = "DS.Drawer";

export default Drawer;
